import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { HashLoader } from 'react-spinners';
import AlertMessage from '../common/AlertMessage/AlertMessage';

import BrandLogo from '../../branding/BrandLogo';
import { resetPasswordApiCall } from '../../services/api/users';
import { APP_ROUTES } from '../../utils/constants/appRoutes';
import { getPasswordStrength } from '../../utils/helpers/helpers';
import LegalLinks from '../legal/LegalLinks';
import './style.css';

interface ResetPasswordParams extends Record<string, string | undefined> {
  token: string;
}

export default function ResetPassword() {
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [passwordStrength, setPasswordStrength] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<{
    show: boolean;
    type: 'success' | 'error';
    message: string;
  }>({
    show: false,
    type: 'success',
    message: '',
  });
  const { token } = useParams<ResetPasswordParams>();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setAlert({ show: true, type: 'error', message: 'Reset token is missing' });
      navigate(APP_ROUTES.AUTH);
    }
  }, [token]);

  const handleSubmit = async (): Promise<void> => {
    if (passwordStrength < 3) {
      setAlert({
        show: true,
        type: 'error',
        message:
          'New password is too weak. Use a combination of upper/lower case letters, numbers and symbols.',
      });
      return;
    }

    if (password !== confirmPassword) {
      setAlert({ show: true, type: 'error', message: "New passwords don't match." });
      return;
    }

    setLoading(true);
    try {
      const apiRes = await resetPasswordApiCall(token as string, {
        password,
        confirmPassword,
      });

      if (apiRes.data.success) {
        setAlert({
          show: true,
          type: 'success',
          message: 'Your password reset was successful! Redirecting to the login page',
        });
        setTimeout(() => navigate(APP_ROUTES.AUTH), 4000);
      } else {
        setAlert({
          show: true,
          type: 'error',
          message: 'Something went wrong, please try again later.',
        });
      }
    } catch (error: any) {
      setAlert({
        show: true,
        type: 'error',
        message: error.response?.data?.message || 'Something went wrong, please try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(getPasswordStrength(newPassword));
  };

  return (
    <div className="limiter">
      <AlertMessage
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert({ ...alert, show: false })}
      />
      <div className="container-login100">
        <div className="wrap-login100">
          <div>
            <BrandLogo />
            <h5 className="Center Margin mb-4">Reset Password</h5>
            <div className="login-fields-container">
              <TextField
                id="outlined-password-input"
                label="New Password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                className="full-width"
              />
              <LinearProgress
                variant="determinate"
                value={(passwordStrength / 5) * 100}
                className="login-fields"
              />
              <TextField
                id="outlined-confirm-password-input"
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="login-fields"
              />
            </div>
            <Button
              size="medium"
              type="submit"
              variant="contained"
              className="full-width"
              disabled={loading || !(password.length && confirmPassword.length)}
              onClick={handleSubmit}
            >
              {loading ? <HashLoader size={30} color="#1b6bae" speedMultiplier={0.8} /> : 'Submit'}
            </Button>
            <div className="mt-2 auth-nav-link">
              <Link to={APP_ROUTES.AUTH}>Return to Sign In</Link>
            </div>
            <div className="mt-2">
              <LegalLinks shortLabels />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
